import React from "react";
import styled from "styled-components";
import { UPSOLVE_INFO } from "../../constants/upsolveInfo";
import LearnListingPanel from "../Learn/LearnListingPanel";
import getMyUpsolveEntryUrl from "../Links/getMyUpsolveEntryUrl";
import AboutPanel from "./AboutPanel";

// Titles should also be links (?) alt would be accordions on mobile or just a super long footer (v0)
//v0: long footer
//v1: titles link out to top item or open triage
//v2: all URLs should use frontdoorUrl for environment-specific routing
//     - internal links: ${frontdoorUrl}/path
//     - external links: full https:// URLs
const frontdoorUrl =
  process.env.NODE_ENV === "production"
    ? "https://upsolve.org"
    : process.env.NODE_ENV === "development"
    ? "https://dev.upsolve.org"
    : "http://localhost:8000";

const columns: { title: string; items: { name: string; url: string; icon?: string }[] }[][] = [
  [
    {
      title: "About Us",
      items: [
        { name: "Team", url: `${frontdoorUrl}/team/` },
        { name: "How Upsolve Works", url: `${frontdoorUrl}/learn/how-upsolve-works/` },
        { name: "Our Story", url: `${frontdoorUrl}/learn/our-story/` },
        { name: "Press", url: `${frontdoorUrl}/press/` },
        { name: "Donate", url: `${frontdoorUrl}/donate/` },
        { name: "Careers", url: `${frontdoorUrl}/careers/` },
        { name: "Contact Us", url: `${frontdoorUrl}/contact/` },
      ],
    },
  ],
  [
    {
      title: "Debt Relief Tools",
      items: [{ name: "File Bankruptcy", url: `${getMyUpsolveEntryUrl()}` }],
    },
    {
      title: "Education",
      items: [
        { name: "Learning Center", url: `${frontdoorUrl}/learn/` },
        { name: "How It's Free", url: `${frontdoorUrl}/learn/how-upsolve-is-free/` },
        { name: "Reviews", url: `${frontdoorUrl}/reviews/` },
      ],
    },
  ],
  [
    {
      title: "Community",
      items: [{ name: "Join Now (verified members)", url: "https://www.facebook.com/groups/upsolveusers/" }],
    },
    {
      title: "Policies & Privacy",
      items: [
        { name: "Editorial Policy", url: `${frontdoorUrl}/editorial-policy/` },
        { name: "Privacy Policy", url: `${frontdoorUrl}/privacy-policy/` },
        { name: "Terms of Service", url: `${frontdoorUrl}/terms-of-service/` },
        { name: "Terms of Use", url: `${frontdoorUrl}/terms-of-use/` },
      ],
    },
  ],
  [
    {
      title: "Mailing Address",
      items: [
        { name: UPSOLVE_INFO.name, url: "" },
        { name: UPSOLVE_INFO.address.street1, url: "" },
        {
          name: `${UPSOLVE_INFO.address.city}, ${UPSOLVE_INFO.address.state}, ${UPSOLVE_INFO.address.zipcode}`,
          url: "",
        },
      ],
    },
  ],
];
const Footer = (props: { showArticles?: boolean; bio?: boolean }): React.ReactElement => {
  return (
    <div>
      {props.showArticles === true && <LearnListingPanel />}
      {props.bio !== false && <AboutPanel />}
      <FooterContainer>
        <div className="footer__directory">
          {columns.map((column) => (
            <div className="footer__directory__column">
              {column.map((columnSection) => (
                <div className="footer__directory__column__section">
                  <h6 className="footer__directory__column__title">{columnSection.title}</h6>
                  <div className="footer__directory__column__section__links">
                    {columnSection.items.map((item) => (
                      <a
                        className={`footer__directory__column__section__link${!item.url ? " inactive" : ""}`}
                        aria-busy="false"
                        href={item.url}
                      >
                        {item.name}
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="footer__divider" />
        <div className="footer__funders">
          <div className="footer__funders__label">
            Upsolve's free bankruptcy tool has been made possible by leading philanthropies including:
          </div>
          <div className="footer__funders__logos">
            <img
              alt="Legal Services Corporation"
              src="/images/lsc.png"
              className="funder-logo"
              loading="lazy"
              decoding="async"
              fetchPriority="low"
            />
            <img
              alt="Robin Hood"
              src="/images/robinhood.png"
              className="funder-logo"
              loading="lazy"
              decoding="async"
              fetchPriority="low"
            />
            <img
              alt="FastForward"
              src="/images/fast-forward.png"
              className="funder-logo"
              loading="lazy"
              decoding="async"
              fetchPriority="low"
            />
            <img
              alt="Bill & Melinda Gates Foundation"
              src="/images/gatesFoundation.png"
              className="funder-logo"
              loading="lazy"
              decoding="async"
              fetchPriority="low"
            />
            <img
              alt="Financial Health Network"
              src="/images/financialHealthNetwork.png"
              className="funder-logo"
              loading="lazy"
              decoding="async"
              fetchPriority="low"
            />
          </div>
        </div>
        <div className="footer__divider" />
        <div className="footer__bottom">
          <div className="footer__bottom__left">
            <div className="footer__disclaimer">
              Upsolve is a 501(c)(3) nonprofit organization that helps low-income and working-class Americans eliminate
              their debt and rebuild credit with our free bankruptcy tool. Upsolve (EIN 82-1736267) isn't a lawyer and
              can't give you <a href={`${frontdoorUrl}/no-legal-advice/`}>any legal advice.</a> No communication between
              you and Upsolve should be considered legal advice. If you need legal advice, you can request{" "}
              <a href={`${UPSOLVE_FILER_URL}/referral/`}>a free consultation</a> with a law firm in our network. By
              using our tools or reading our site, you don't enter any form of attorney-client relationship with
              Upsolve. And your use is always subject to our <a href={`${frontdoorUrl}/terms-of-use/`}>Terms of Use</a>{" "}
              and <a href={`${frontdoorUrl}/terms-of-service/`}>Terms of Service</a>. © {new Date().getFullYear()}.
            </div>
          </div>
          <div className="footer__social">
            <a
              href="https://www.youtube.com/upsolve"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
              className="footer__social__icon"
            >
              <img
                src="/images/icon-youtube.svg"
                alt=""
                width={24}
                height={24}
                loading="lazy"
                decoding="async"
                fetchPriority="low"
              />
            </a>
            <a
              href="https://www.facebook.com/upsolvebankruptcy/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="footer__social__icon"
            >
              <img
                src="/images/icon-facebook.svg"
                alt=""
                width={24}
                height={24}
                loading="lazy"
                decoding="async"
                fetchPriority="low"
              />
            </a>
            <a
              href="https://www.instagram.com/upsolve/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="footer__social__icon"
            >
              <img
                src="/images/icon-instagram.svg"
                alt=""
                width={24}
                height={24}
                loading="lazy"
                decoding="async"
                fetchPriority="low"
              />
            </a>
          </div>
        </div>
      </FooterContainer>
    </div>
  );
};

const FooterContainer = styled.footer`
  background: ${(props) => props.theme.colors.brand[800]};
  color: black;
  display: flex;
  flex-direction: column;
  padding: 64px;
  gap: 48px;
  @media (max-width: ${(props) => props.theme.breakpoints[750]}) {
    padding: 30px;
    gap: 32px;
    /* room for the sticky mobile menu */
    padding-bottom: 86px;
  }
  .footer__directory {
    display: flex;
    flex: 1;
    gap: 60px;
    @media (max-width: ${(props) => props.theme.breakpoints[750]}) {
      flex-direction: column;
      gap: 40px;
    }

    .footer__directory__column {
      display: flex;
      flex-direction: column;
      gap: 40px;
      flex: 1;

      .footer__directory__column__section {
        display: flex;
        flex-direction: column;
        gap: 10px;
        color: black;
        .footer__directory__column__title {
          color: black;
          font-weight: 400;
          font-size: 16px;
          border-bottom: 1px solid ${(p) => p.theme.colors.gray[800]};
          width: 100%;
          max-width: 330px;
        }
        .footer__directory__column__section__links {
          display: flex;
          flex-direction: column;
          gap: 12px;
          @media (max-width: ${(props) => props.theme.breakpoints[750]}) {
            gap: 4px;
          }
        }
        .footer__directory__column__section__link {
          color: black;
          font-weight: 200;
          font-size: 16px;
          display: flex;
          align-items: center;
          gap: 8px;
          a:visited,
          a:active,
          a:hover {
            color: black;
          }
          &:hover {
            text-decoration: underline;
          }
          .social-icon {
            width: 18px;
            height: 18px;
            margin-right: 6px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }
        }
        .inactive {
          color: ${(p) => p.theme.colors.gray[800]};
          cursor: none !important;
          pointer-events: none;
          a:visited,
          a:active,
          a:hover {
            color: ${(p) => p.theme.colors.brand[700]} !important;
          }
          &:hover {
            text-decoration: none !important;
          }
        }
      }
    }
  }
  .footer__divider {
    height: 1px;
    width: 100%;
    background: ${(p) => p.theme.colors.gray[800]};
    margin: 0;
  }
  .footer__funders {
    display: flex;
    flex-direction: column;
    gap: 20px;
    .footer__funders__label {
      color: black;
      font-weight: 400;
      font-size: 16px;
      text-align: center;
    }
    .footer__funders__logos {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 32px;
      flex-wrap: wrap;
      .funder-logo {
        max-height: 50px;
        max-width: 180px;
        height: auto;
        width: auto;
        object-fit: contain;
        flex-shrink: 0;
        filter: brightness(0.95);
      }
      .funder-text {
        color: black;
        font-weight: 200;
        font-size: 16px;
      }
    }
  }
  .footer__bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    flex-wrap: wrap;
    @media (max-width: ${(props) => props.theme.breakpoints[750]}) {
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
    }
  }
  .footer__bottom__left {
    flex: 1;
    max-width: calc(100% - 120px);
    @media (max-width: ${(props) => props.theme.breakpoints[750]}) {
      max-width: 100%;
    }
  }
  .footer__disclaimer {
    color: black;
    font-weight: 200;
    font-size: 16px;
    line-height: 1.5;
    a,
    a:visited,
    a:active,
    a:hover {
      color: black;
      text-decoration: underline;
    }
  }
  .footer__social {
    display: flex;
    gap: 16px;
    align-items: center;
    flex-shrink: 0;
    .footer__social__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      color: black;
      opacity: 0.8;
      transition: opacity 0.2s;
      &:hover {
        opacity: 1;
      }
      svg {
        width: 100%;
        height: 100%;
      }
    }
  }
`;

export default Footer;
